<script lang="ts" setup>
import { SolModal } from '@solfacil/girassol'
import { useToast } from '@solfacil/girassol'
import IconDownload from '~icons/girassol/download'
import IconDocument from '~icons/material-symbols/description-outline'

interface DocumentItem {
  id: string
  name: string
  url: string
  type: 'pdf' | 'image'
}

interface Props {
  isOpen: boolean
  customerBiometrics: any
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const DOCUMENT_TYPES_CONFIG = [
  {
    key: 'contract',
    contractKey: 'loan',
    name: 'Contrato',
    isList: false,
  },
  {
    key: 'terms',
    contractKey: 'combo_connect_term',
    name: 'Termo de aceite',
    isList: false,
  },
  {
    key: 'insurance',
    contractKey: 'lender_insurance',
    name: 'Apólice de seguro',
    isList: false,
  },
  {
    key: 'TAF',
    contractKey: 'financing_amendment_term',
    name: 'Termo de aditivo de financiamento - TAF',
    isList: true,
  },
] as const

const { t } = useI18n()
const toast = useToast()

const downloadingFiles = ref<Set<string>>(new Set())

function processDocumentsFromConfig(config: typeof DOCUMENT_TYPES_CONFIG, data: any) {
  const documents: Array<{ id: string, name: string, url: string, type: 'pdf' }> = []

  config.forEach((configItem) => {
    const { key, contractKey, name, isList } = configItem
    const contractData = data?.contracts?.[contractKey]

    if (!contractData) {
      return
    }

    if (isList && Array.isArray(contractData)) {
      contractData.forEach((item: any, index: number) => {
        if (item?.status === 'signed' && item?.document_signed) {
          documents.push({
            id: `${key}-${index + 1}`,
            name: `${name} ${index + 1}`,
            url: item.document_signed,
            type: 'pdf' as const,
          })
        }
      })
    }
    else if (!isList && contractData?.status === 'signed' && contractData?.document_signed) {
      const displayName = key === 'terms' ? 'Termos de aceite do Combo Conecte' : name

      documents.push({
        id: key,
        name: displayName,
        url: contractData.document_signed,
        type: 'pdf' as const,
      })
    }
  })

  return documents
}

const documentsList = computed(() => processDocumentsFromConfig(DOCUMENT_TYPES_CONFIG, props.customerBiometrics))
const hasDocuments = computed(() => documentsList.value.length > 0)

function handleClose() {
  emit('close')
}

function createDownloadLink(blobUrl: string, fileName: string): HTMLAnchorElement {
  const link = window.document.createElement('a')
  link.href = blobUrl
  link.download = fileName
  link.style.display = 'none'
  return link
}

function triggerDownload(link: HTMLAnchorElement) {
  window.document.body.appendChild(link)
  link.click()
  window.document.body.removeChild(link)
}

async function downloadFile(documentItem: DocumentItem): Promise<void> {
  try {
    const response = await fetch(documentItem.url)
    const blob = await response.blob()
    const blobUrl = window.URL.createObjectURL(blob)
    const link = createDownloadLink(blobUrl, documentItem.name)

    triggerDownload(link)
    window.URL.revokeObjectURL(blobUrl)

    toast.createSuccessToast({
      title: 'Arquivo baixado com sucesso',
      description: 'O arquivo foi baixado com sucesso. Você pode encontrá-lo na pasta de downloads do seu computador.',
    })
  }
  catch (error) {
    console.error('Erro ao baixar arquivo:', error)
    toast.createErrorToast({
      title: 'Erro ao baixar arquivo',
      description: 'Não foi possível baixar o arquivo. Tente novamente mais tarde.',
    })
  }
}

async function handleDownload(documentItem: DocumentItem) {
  if (downloadingFiles.value.has(documentItem.id)) {
    return
  }
  downloadingFiles.value.add(documentItem.id)

  try {
    await downloadFile(documentItem)
  }
  finally {
    downloadingFiles.value.delete(documentItem.id)
  }
}

function handlePreview(documentItem: DocumentItem) {
  window.open(documentItem.url, '_blank')
}

function isDownloading(documentId: string): boolean {
  return downloadingFiles.value.has(documentId)
}

function getDownloadButtonText(documentId: string): string {
  return isDownloading(documentId) ? 'Baixando...' : 'Baixar'
}
</script>

<template>
  <SolModal
    id="documents-modal"
    :is-open="isOpen"
    title="Documentos"
    :size="{ desktop: 'large', mobile: 'full' }"
    @close="handleClose"
  >
    <div class="documents-container">
      <!-- Document list -->
      <div v-if="hasDocuments" class="documents-list">
        <div
          v-for="document in documentsList"
          :key="document.id"
          class="document-item"
        >
          <div class="document-info">
            <IconDocument class="document-icon" />
            <span class="document-name">{{ document.name }}</span>
          </div>

          <div class="document-actions">
            <SolButton
              :id="`preview-${document.id}`"
              size="small"
              variant="secondary"
              @click="handlePreview(document)"
            >
              {{ t('customer.biometry.footer.view') }}
            </SolButton>

            <SolButton
              :id="`download-${document.id}`"
              size="small"
              variant="primary"
              :loading="isDownloading(document.id)"
              :disabled="isDownloading(document.id)"
              @click="handleDownload(document)"
            >
              <template #icon:left>
                <IconDownload />
              </template>
              {{ getDownloadButtonText(document.id) }}
            </SolButton>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else class="empty-state">
        <IconDocument class="empty-icon" />
        <p class="empty-text">
          Nenhum documento disponível.
        </p>
      </div>
    </div>
  </SolModal>
</template>

<style lang="scss" scoped>
.documents-container {
  @apply p-4;
}

.documents-list {
  @apply space-y-4;
}

.document-item {
  @apply flex flex-col md:system:flex-row md:system:items-center md:system:justify-between p-4 border border-neutral-low-light rounded-lg;
  @apply hover:bg-neutral-high-pure transition-colors duration-200;
  @apply gap-3;
}

.document-info {
  @apply flex items-center gap-3;
  @apply flex-1 min-w-0;
}

.document-icon {
  @apply w-6 h-6 text-neutral-low-dark;
}

.document-name {
  @apply font-bold text-neutral-low-dark;
}

.document-actions {
  @apply flex items-center gap-2;
  @apply w-full md:system:w-auto;
  @apply flex-shrink-0;
}

.empty-state {
  @apply flex flex-col items-center justify-center py-12 text-center;
}

.empty-icon {
  @apply w-16 h-16 text-neutral-low-light mb-4;
}

.empty-text {
  @apply text-neutral-low-light;
}
</style>
